<template>
  <div class="app-container">
    <Container
      :title="t('route.specialPaymentRequestPO')"
      :hasLine="true"
    >
      <template #header>
        <SearchForm
          :formModel="searchFormModel"
          :formStruct="searchFormStruct"
          :hasCreate="false"
          :submitButton="t('common.search')"
          :resetButton="t('common.reset')"
          @submit="submitSearch"
          @reset="resetForm"
        />
      </template>
      <template #main>
        <el-row>
          <el-col :span="12">
            <el-dropdown
              v-if="$hasPermission(['APPLY_SPPO_SW', 'APPLY_SPPO_W'])"
              class="avatar-container right-menu-item hover-effect"
              trigger="click"
            >
              <div class="avatar-wrapper">
                <el-button
                  ref="opsButton"
                  class="order-button"
                  type="primary"
                  size="large"
                >
                  {{ t('order.orderOperations') }}
                </el-button>
              </div>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item
                    @click="handlePaymentDate"
                  >
                    <span style="display:block;">{{ t('order.setPaymentDate') }}</span>
                  </el-dropdown-item>

                  <el-dropdown-item
                    divided
                    @click="cancelApplication"
                  >
                    <span style="display:block;">{{ t('order.cancelApplication') }}</span>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            <el-dropdown
              v-if="$hasPermission(['APPLY_SPPO_SW', 'APPLY_SPPO_W', 'APPLY_SUB_W'])"
              trigger="click"
              style="margin-left: 20px"
            >
              <el-button
                class="order-button"
                type="primary"
                size="large"
              >
                {{ t('order.orderFunctions') }}
              </el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item
                    v-if="$hasPermission(['APPLY_SPPO_SW', 'APPLY_SPPO_W'])"
                    @click="onCreate"
                  >
                    <span style="display:block;">{{ t('common.append') }}</span>
                  </el-dropdown-item>
                  <el-upload
                    v-if="$hasPermission(['APPLY_SPPO_SW', 'APPLY_SPPO_W'])"
                    class="upload-demo"
                    :action="getUploadUri()"
                    :headers="getUploadHeader()"
                    :on-success="handleAvatarSuccess"
                    :on-error="handleAvatarError"
                    :show-file-list="false"
                    accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    :before-upload="checkFile"
                  >
                    <el-dropdown-item divided>
                      <span style="display:flex;">{{ t('order.batchUpload') }}</span>
                    </el-dropdown-item>
                  </el-upload>
                  <el-dropdown-item
                    v-if="$hasPermission(['APPLY_SUB_W'])"
                    divided
                    @click="generateApprovalForm"
                  >
                    <span style="display:block;">{{ t('order.generateApprovalForm') }}</span>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </el-col>
          <el-col
            :span="12"
            style="display: flex;justify-content: flex-end;align-items: center;"
          >
            <el-button
              type="primary"
              plain
              @click="handleExport"
            >
              {{ t('common.downloadTemplate') }}
            </el-button>
          </el-col>
        </el-row>
        <SimpleTable
          ref="tableRef"
          :listLoading="listLoading"
          :tableData="tableData"
          :columnStruct="columnStruct"
          :hadSelect="true"
        >
          <template
            #default="slotProps"
          >
            <el-button
              v-if="$hasPermission(['APPLY_SPPO_SW', 'APPLY_SPPO_W'])"
              @click="handleEdit(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('common.edit') }}
            </el-button>
          </template>
        </SimpleTable>
        <el-pagination
          :total="total"
          v-show="total > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :page-size="limit"
          :current-page="page"
          :page-sizes="[10, 20, 50, 100, 200]"
          layout="total, sizes, prev, pager, next, jumper"
          style="text-align: right;"
        />
      </template>
    </Container>
    <BaseDialog
      v-model="datePickerDialogVisible"
      :title="t('order.setPaymentDate')"
      :autoClose="false"
      width="30%"
      content-center
      destroy-on-close
      @confirm="onDatePickerDialogConfirm"
    >
      <template #default>
        <el-date-picker
          v-model="selectedDate"
          type="date"
          :placeholder="t('common.selectDateTip')"
          :default-value="new Date()"
          :show-time="false"
          :size="large"
        />
      </template>
    </BaseDialog>
    <BaseDialog
      v-model="purposeDialogVisible"
      :title="t('approvalForm.purpose')"
      :autoClose="false"
      width="30%"
      destroy-on-close
      @confirm="onPurposeDialogConfirm"
      @close="onDialogClose"
    >
      <template #default>
        <el-row>
          <el-input
            v-model="purpose"
            type="textarea"
            rows="5"
            maxlength="100"
            show-word-limit
            :placeholder="t('order.purposePayeeMayViewP')"
          />
        </el-row>
        <el-row style="margin-top: 10px">
          <el-col
            :offset="18"
            style="color: #FF4949"
          >
            {{ t('common.max100WordsTip') }}
          </el-col>
        </el-row>
      </template>
    </BaseDialog>
    <GenerateApprovalPoDialog
      v-if="generateApprovalDialogVisible"
      v-model="generateApprovalDialogVisible"
      v-model:groupedPoList="selectedRows"
      @refresh="onDialogConfirm"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, toRefs, onMounted, ref, onActivated } from 'vue'
import { useI18n } from 'vue-i18n'
import { createModel } from '@/utils/component'
import { checkPermission } from '@/utils/permission'
import { formatDate, formatThousands } from '@/utils'
import Container from '@/components/form/container/Container.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import BaseDialog from '@/components/dialog/BaseDialog.vue'
import { ElMessage } from 'element-plus'
import GenerateApprovalPoDialog
  from '@/views/application-form/special-payment-request-po/GenerateApprovalPODialog.vue'
import dayjs from 'dayjs'
import {
  PORequestIdListModel,
  PORequestModel, PORequestPageParamModel, PORequestSetPaymentDateModel, POSetPurposeParamModel
} from '@/model/application-form/specialPaymentRequestPO'
import {
  cancelPOApplication, excelPOFileUpload, exportPOTemplate, getPORequestPageList, setPORequestPaymentDate,
  setPOSetPurpose, getPORequestPageListByUser
} from '@/apis/application-form/specialPaymentRequestPO'
import router from '@/router'
import { RootObject } from '@/model/rootObject'
import { useStore } from 'vuex'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'

export default defineComponent({
  name: 'SpecialPaymentRequestPOList',
  components: {
    GenerateApprovalPoDialog,
    BaseDialog,
    SearchForm,
    SimpleTable,
    Container
  },
  setup() {
    const { t } = useI18n()
    const tableRef = ref()
    const selectedRows = ref<PORequestModel[]>()
    const dialogKey = ref(0)
    const searchStruct = [
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      { prop: 'vendorName', label: t('supplier.supplierName'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'paymentDate',
        label: t('order.paymentDate'),
        component: 'el-date-picker',
        type: 'date',
        model: '',
        suffix: '前'
      },
      { prop: 'currency', label: t('bank.currency'), component: 'el-input', type: 'text', model: '' }
    ]
    const orderTableStruct = [
      { prop: 'id', label: t('common.indexNumber'), width: '80px' },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'vendorName', label: t('supplier.supplierName'), width: '200px' },
      { prop: 'specialPaymentType', label: t('approvalForm.specialPaymentType'), width: '200px' },
      { prop: 'reference', label: t('order.invoiceNumber') },
      { prop: 'amount', label: t('bank.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'currency', label: t('bank.currency'), width: '80px' },
      {
        prop: 'paymentDate',
        label: t('order.paymentDate'),
        formatter: (row: PORequestModel) => {
          return formatDate(row.paymentDate, 'yyyy-MM-dd')
        }
      },
      { prop: 'comment', label: t('common.notes') },
      { prop: 'purpose', label: t('order.purposePayeeMayView'), width: '200px' }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as PORequestPageParamModel,
      tableData: Array<PORequestModel>(),
      columnStruct: orderTableStruct,
      datePickerDialogVisible: false,
      purposeDialogVisible: false,
      generateApprovalDialogVisible: false,
      changePurposeId: null as number | null,
      purpose: null as string | null,
      selectedDate: null as Date | null,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true
        let dataStr = null
        if (dataMap.searchFormModel.paymentDate) {
          dataStr = dayjs(dataMap.searchFormModel.paymentDate).endOf('day').format('YYYY-MM-DD HH:mm:ss SSS')
        }
        const params: PORequestPageParamModel = {
          companyCode: dataMap.searchFormModel.companyCode,
          vendorName: dataMap.searchFormModel.vendorName,
          paymentDate: dataStr,
          currency: dataMap.searchFormModel.currency,
          isProcess: 0,
          active: true,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        // const data = await getPORequestPageList(params)
        let data = null
        if (checkPermission(['APPLY_SPPO_R'])) {
          data = await getPORequestPageList(params)
        } else if (checkPermission(['APPLY_SPPO_SR'])) {
          data = await getPORequestPageListByUser(params)
        }
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
        }
        dataMap.total = data?.data.totalCount ?? 0
        tableRef.value.clearSelection()
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      },
      async handleAvatarSuccess(response: RootObject<number>, file: any) {
        if (response.code === 200 && response.data > 0) {
          ElMessage({
            message: t('fileUpload.uploadSuccess'),
            type: 'success',
            duration: 2000
          })
          await dataMap.getPageList(dataMap.page, dataMap.limit)
        } else if (response.msg && response.msg !== 'success') {
          ElMessage({
            message: response.msg,
            type: 'error',
            duration: 2000
          })
        } else {
          ElMessage({
            message: file.name + ':' + t('fileUpload.uploadError'),
            type: 'error',
            duration: 2000
          })
        }
      },
      handleAvatarError(response: RootObject<number>, file: any) {
        if (response.msg) {
          ElMessage({
            message: response.msg,
            type: 'error',
            duration: 2000
          })
        } else {
          ElMessage({
            message: file.name + ':' + t('fileUpload.uploadError'),
            type: 'error',
            duration: 2000
          })
        }
      },
      getUploadUri() {
        return excelPOFileUpload()
      },
      getUploadHeader() {
        return {
          Authorization: useStore().state.user.token
        }
      },
      checkFile(file: any) {
        if (file) {
          console.log('file type')
          console.log(file)
        }
      },
      async handleExport() {
        const urlBlob = await exportPOTemplate()
        if (urlBlob) {
          const link = document.createElement('a')
          link.href = urlBlob
          link.download = '特殊付款申请模板(PO).xlsx'
          link.click()
          window.URL.revokeObjectURL(urlBlob)
        } else {
          ElMessage({
            message: '获取excel失败',
            type: 'error',
            duration: 2000
          })
        }
      }
    })
    const handlePaymentDate = () => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        dataMap.selectedDate = null
        dataMap.datePickerDialogVisible = true
      } else {
        ElMessage({
          message: t('order.selectTip'),
          type: 'error'
        })
      }
    }
    const cancelApplication = async() => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        const params: PORequestIdListModel = { idList: tableRef.value.getSelectionRows().map((row: any) => row.id) }
        const res = await cancelPOApplication(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.cancelSuccess'),
            type: 'success',
            duration: 2000
          })
          await dataMap.getPageList(null, 10)
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      } else {
        ElMessage({
          message: t('order.selectTip'),
          type: 'error'
        })
      }
    }
    const onCreate = () => {
      router.push({ name: 'CreateSpecialPaymentRequestPO' })
    }
    const batchUpload = () => {
      console.log('batchUpload')
    }
    const generateApprovalForm = () => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        selectedRows.value = tableRef.value.getSelectionRows().map((row: any) => row.id)
        dataMap.generateApprovalDialogVisible = true
      } else {
        ElMessage({
          message: t('order.selectTip'),
          type: 'error'
        })
      }
    }
    const handleEdit = (row?: PORequestModel) => {
      if (row) {
        router.push({ path: `update/${row.id}` })
      }
    }
    const onDialogConfirm = async() => {
      await dataMap.getPageList(dataMap.page, dataMap.limit)
    }
    const onDialogClose = async() => {
      await dataMap.getPageList(dataMap.page, dataMap.limit)
    }
    const onDatePickerDialogConfirm = async() => {
      if (dataMap.selectedDate != null) {
        const params: PORequestSetPaymentDateModel = {
          idList: tableRef.value.getSelectionRows().map((row: any) => row.id),
          paymentDate: formatDate(dataMap.selectedDate)
        }
        const res = await setPORequestPaymentDate(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.setSuccess'),
            type: 'success',
            duration: 2000
          })
          dataMap.datePickerDialogVisible = false
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
        await dataMap.getPageList(dataMap.page, dataMap.limit)
      } else {
        ElMessage({
          message: t('order.setPaymentDateP'),
          type: 'error'
        })
      }
    }
    const onPurposeDialogConfirm = async() => {
      if (dataMap.changePurposeId != null && dataMap.purpose != null) {
        const params: POSetPurposeParamModel = {
          id: dataMap.changePurposeId,
          purpose: dataMap.purpose
        }
        const res = await setPOSetPurpose(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.setSuccess'),
            type: 'success',
            duration: 2000
          })
          dataMap.purposeDialogVisible = false
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
        await dataMap.getPageList(dataMap.page, dataMap.limit)
      }
    }
    const getCompanyList = async() => {
      dataMap.searchFormStruct[0].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.searchFormStruct[0].options = optionList
      dataMap.searchFormStruct[0].loading = false
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
      getCompanyList()
    })
    onActivated(async() => {
      if (history.state?.refresh === true) {
        await dataMap.getPageList()
        // 用完删除
        const state = { ...history.state }
        delete state.refresh
        history.replaceState(state, '')
      }
    })
    return {
      t,
      tableRef,
      dialogKey,
      selectedRows,
      handlePaymentDate,
      cancelApplication,
      onCreate,
      batchUpload,
      generateApprovalForm,
      handleEdit,
      onDatePickerDialogConfirm,
      onDialogConfirm,
      onPurposeDialogConfirm,
      onDialogClose,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style
  scoped
  lang="scss"
>
.order-button {
  margin-bottom: 10px;
}

:deep(.el-upload.el-upload--text) {
  width: 100%;
}
</style>
